import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/pos-api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, ShieldCheck, Smartphone } from "lucide-react";

export function useSupervisorApproval() {
  const [request, setRequest] = useState(null);
  const resolver = useRef(null);

  const finish = useCallback((value) => {
    setRequest(null);
    const resolve = resolver.current;
    resolver.current = null;
    resolve?.(value);
  }, []);

  useEffect(() => {
    if (!request?.id) return undefined;
    const poll = async () => {
      try {
        const { data } = await api.get(`/supervisor/approvals/${request.id}`);
        if (data.status === "approved") finish({ headers: { "X-Supervisor-Approval": request.id } });
        if (["rejected", "expired"].includes(data.status)) finish(false);
      } catch { /* jaringan sementara tidak membatalkan permintaan */ }
    };
    const timer = window.setInterval(poll, 2000);
    poll();
    return () => window.clearInterval(timer);
  }, [request?.id, finish]);

  const approveAction = useCallback(async (action, summary) => {
    const { data } = await api.post("/supervisor/approvals", { action, summary });
    if (!data.required) return {};
    setRequest(data);
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const dialog = (
    <Dialog open={!!request} onOpenChange={(open) => { if (!open) finish(false); }}>
      <DialogContent className="max-w-sm bg-ui-card border-ui-line text-ui-text">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-warning-readable" /> Menunggu Supervisor</DialogTitle>
          <DialogDescription className="text-ui-muted">Permintaan telah dikirim. Supervisor dapat menyetujuinya dari HP tanpa memberikan PIN kepada kasir.</DialogDescription>
        </DialogHeader>
        <div className="rounded-xl border border-ui-line bg-ui-soft p-4 space-y-3 text-sm">
          <div className="flex items-center gap-3"><Smartphone className="h-6 w-6 text-brand-readable" /><div><div className="font-semibold">Buka menu Persetujuan Supervisor</div><div className="text-xs text-ui-muted">Masukkan PIN pada HP supervisor.</div></div></div>
          <div className="border-t border-ui-line pt-3"><div className="text-xs text-ui-muted">Tindakan</div><div className="font-medium">{request?.action_label}</div><div className="mt-1 text-xs text-ui-muted">{request?.summary}</div></div>
          <div className="flex items-center gap-2 text-warning-readable"><Loader2 className="h-4 w-4 animate-spin" /> Menunggu persetujuan (maksimal 2 menit)…</div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => finish(false)} className="border-ui-line bg-transparent">Batalkan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
  return { approveAction, approvalDialog: dialog };
}
